import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  LocalOffer,
  Schedule,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  getSales,
  createSale,
  updateSale,
  deleteSale,
  clearError,
  clearSuccess,
} from '../store/slices/saleSlice';
import { compressImage } from '../utils/imageCompressor';
import { getProducts } from '../store/slices/productSlice';

const Sales = () => {
  const dispatch = useDispatch();
  const { sales, isLoading, error, success } = useSelector((state) => state.sales);
  const { products: allProducts = [], isLoading: productsLoading } = useSelector((state) => state.products || {});

  const [openDialog, setOpenDialog] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    discountPercentage: 0,
    originalPrice: 0,
    salePrice: 0,
    category: '',
    products: [],
    image: '',
    bannerColor: '#ff4444',
    textColor: '#ffffff',
    startDate: '',
    endDate: '',
    isActive: true,
    priority: 1,
    showCountdown: true,
    countdownPosition: 'header',
    link: '',
    product: '', // Product ID or slug for navigation
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    setEditingSale(null);
    setFormData({
      title: '',
      description: '',
      discountPercentage: 0,
      originalPrice: 0,
      salePrice: 0,
      category: '',
      products: [],
      image: '',
      bannerColor: '#ff4444',
      textColor: '#ffffff',
      startDate: '',
      endDate: '',
      isActive: true,
      priority: 1,
      showCountdown: true,
      countdownPosition: 'header',
      link: '',
      product: '',
    });
    setUploadingImage(false);
    setImagePreview(null);
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getSales());
    // Fetch all products for the dropdown
    dispatch(getProducts({ showAll: 'true', limit: 1000 }));
  }, [dispatch]);

  // Handle success state
  useEffect(() => {
    if (success) {
      handleCloseDialog();
      dispatch(clearSuccess());
      dispatch(getSales());
    }
  }, [success, dispatch, handleCloseDialog]);

  // Handle error state
  useEffect(() => {
    if (error) {
      console.error('Sale error:', error);
    }
  }, [error]);

  const handleOpenDialog = (sale = null) => {
    if (sale) {
      setEditingSale(sale);
      setFormData({
        title: sale.title || '',
        description: sale.description || '',
        discountPercentage: sale.discountPercentage || 0,
        originalPrice: sale.originalPrice || 0,
        salePrice: sale.salePrice || 0,
        category: sale.category || '',
        products: sale.products || [],
        image: sale.image || '',
        bannerColor: sale.bannerColor || '#ff4444',
        textColor: sale.textColor || '#ffffff',
        startDate: sale.startDate ? sale.startDate.split('T')[0] : '',
        endDate: sale.endDate ? sale.endDate.split('T')[0] : '',
        isActive: sale.isActive !== undefined ? sale.isActive : true,
        priority: sale.priority || 1,
        showCountdown: sale.showCountdown !== undefined ? sale.showCountdown : true,
        countdownPosition: sale.countdownPosition || 'header',
        link: sale.link || '',
        product: sale.product || sale.link || '', // Support both product and link for backward compatibility
      });
      setImagePreview(sale.image || null);
    } else {
      setEditingSale(null);
      setFormData({
        title: '',
        description: '',
        discountPercentage: 0,
        originalPrice: 0,
        salePrice: 0,
        category: '',
        products: [],
        image: '',
        bannerColor: '#ff4444',
        textColor: '#ffffff',
        startDate: '',
        endDate: '',
        isActive: true,
        priority: 1,
        showCountdown: true,
        countdownPosition: 'header',
        link: '',
        product: '',
      });
      setImagePreview(null);
    }
    setOpenDialog(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };

      // If admin selects a product for navigation, auto-use that product's main image
      // so the sale banner image is always in sync with the product (no extra upload needed).
      if (name === 'product' && value) {
        const selectedProduct = allProducts.find(
          (p) => (p.slug || p._id) === value
        );

        if (selectedProduct && selectedProduct.images && selectedProduct.images[0]) {
          const img = selectedProduct.images[0];

          // If DB already stores full URL, use it; otherwise prefix with API base (same pattern as SellerDashboard)
          if (typeof img === 'string' && (img.startsWith('http://') || img.startsWith('https://'))) {
            updated.image = img;
          } else {
            const apiBase = (process.env.REACT_APP_API_URL || 'https://xaintro-backend.onrender.com').replace('/api', '');
            const normalized = img && img.startsWith('/') ? img : `/${img || ''}`;
            updated.image = `${apiBase}${normalized}`;
          }
        }
      }

      return updated;
    });
  };

  // Helper: convert plain sale data into FormData so backend Multer/Cloudinary middleware always works
  const buildSaleFormData = (data) => {
    const formDataObj = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      if (value === undefined || value === null) return;

      // Arrays (e.g. products) or nested objects → send as JSON string
      if (Array.isArray(value) || (typeof value === 'object' && !(value instanceof File))) {
        formDataObj.append(key, JSON.stringify(value));
      } else {
        formDataObj.append(key, String(value));
      }
    });

    return formDataObj;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(clearError());

    // Validate required fields
    if (!formData.title || !formData.description || !formData.startDate || !formData.endDate) {
      alert('Please fill in all required fields (Title, Description, Start Date, End Date)');
      return;
    }

    const submitData = {
      title: formData.title,
      description: formData.description,
      discountPercentage: Number(formData.discountPercentage) || 0,
      originalPrice: Number(formData.originalPrice) || 0,
      salePrice: Number(formData.salePrice) || 0,
      category: formData.category || '',
      products: Array.isArray(formData.products) ? formData.products : [],
      image: formData.image || '',
      bannerColor: formData.bannerColor || '#ff4444',
      textColor: formData.textColor || '#ffffff',
      startDate: new Date(formData.startDate).toISOString(),
      endDate: new Date(formData.endDate).toISOString(),
      isActive: formData.isActive === true || formData.isActive === 'true',
      priority: Number(formData.priority) || 1,
      showCountdown: formData.showCountdown === true || formData.showCountdown === 'true',
      countdownPosition: formData.countdownPosition || 'header',
      link: formData.link || '',
      product: formData.product || '', // Product ID or slug for navigation
    };

    try {
      const formPayload = buildSaleFormData(submitData);

      if (editingSale && editingSale._id) {
        await dispatch(updateSale({ id: editingSale._id, saleData: formPayload })).unwrap();
        // Success handled by useEffect
      } else {
        await dispatch(createSale(formPayload)).unwrap();
      }
    } catch (error) {
      console.error('Error saving sale:', error);
      // Error handled by Redux state
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this sale?')) {
      dispatch(deleteSale(id));
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    setUploadingImage(true);

    try {
      console.log('[Sales] Starting image upload...', {
        name: file.name,
        size: file.size,
        type: file.type,
      });

      // Compress image before upload (same logic as ProductForm)
      let fileToUpload = file;
      try {
        console.log('[Sales] Compressing image...');
        fileToUpload = await compressImage(file, {
          maxSizeMB: 0.5,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        });
        console.log('[Sales] Compression complete', {
          originalSize: file.size,
          compressedSize: fileToUpload.size,
        });
      } catch (compressionError) {
        console.warn('[Sales] Compression failed, using original file:', compressionError);
        // Continue with original file if compression fails
      }

      const formData = new FormData();
      formData.append('image', fileToUpload);

      const apiBase = process.env.REACT_APP_API_URL || 'https://xaintro-backend.onrender.com';
      const url = `${apiBase}/upload`;
      console.log('[Sales] Uploading to:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      console.log('[Sales] /upload response status:', response.status, response.statusText);

      if (!response.ok) {
        let errorBody;
        try {
          errorBody = await response.json();
        } catch (parseErr) {
          errorBody = await response.text();
        }
        console.error('[Sales] /upload error response body:', errorBody);
        const message =
          (errorBody && errorBody.message) ||
          (typeof errorBody === 'string' ? errorBody : '') ||
          'Upload failed';
        throw new Error(message);
      }

      const result = await response.json();
      console.log('[Sales] /upload success payload:', result);
      const imageUrl = result.imageUrl;

      setFormData((prev) => ({
        ...prev,
        image: imageUrl,
      }));
      setImagePreview(imageUrl);
    } catch (error) {
      console.error('[Sales] Image upload error:', error);
      alert(`Failed to upload image: ${error.message || 'Unknown error'}`);
    } finally {
      setUploadingImage(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Invalid Date';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      return 'Invalid Date';
    }
  };

  const getTimeRemaining = (endDate) => {
    const now = new Date().getTime();
    const end = new Date(endDate).getTime();
    const timeLeft = end - now;

    if (timeLeft < 0) return 'Expired';

    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));

    return `${days}d ${hours}h ${minutes}m`;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            color: '#000000',
            fontWeight: 700,
            pb: 2,
            borderBottom: '2px solid #ff2852',
          }}
        >
          Sales Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{
            backgroundColor: '#ff2852',
            color: 'white',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#5A189A',
            },
          }}
        >
          Add Sale
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => dispatch(clearSuccess())}>
          {success}
        </Alert>
      )}

      {sales.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 4,
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            border: '2px dashed #dee2e6',
          }}
        >
          <LocalOffer sx={{ fontSize: 64, color: '#6c757d', mb: 2 }} />
          <Typography variant="h5" color="text.secondary" gutterBottom>
            No Sales Found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Create your first sale to start promoting your products with special offers and discounts.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{
              backgroundColor: '#FF2852',
              color: 'var(--xaintro-black)',
              '&:hover': {
                backgroundColor: 'var(--xaintro-gold-dark)',
              },
            }}
          >
            Create Your First Sale
          </Button>
        </Box>
      ) : (
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,
          justifyContent: 'flex-start',
          '@media (max-width: 600px)': {
            justifyContent: 'center',
            gap: 2,
          }
        }}>
          {sales.map((sale) => (
            <Card
              key={sale._id}
              sx={{
                width: { xs: '100%', sm: '350px' }, // Full width on mobile, fixed width on larger screens
                maxWidth: '350px', // Ensure it doesn't get too wide
                height: 'auto',
                display: 'flex',
                flexDirection: 'column',
                border: sale.isActive ? '2px solid #4caf50' : '1px solid #e0e0e0',
                backgroundColor: sale.isActive ? '#f8fff8' : 'white',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                },
              }}
            >
              {sale.image && (
                <CardMedia
                  component="img"
                  height="200"
                  image={sale.image}
                  alt={sale.title}
                  sx={{ objectFit: 'cover' }}
                />
              )}
              <CardContent sx={{ flexGrow: 1, p: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                      fontWeight: 600,
                      fontSize: '1rem',
                      lineHeight: 1.2,
                      maxWidth: '75%'
                    }}
                  >
                    {sale.title}
                  </Typography>
                  <Box display="flex" gap={0.5}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(sale)}
                        sx={{
                          color: '#ff2852',
                          '&:hover': { backgroundColor: 'rgba(123, 44, 191, 0.1)' }
                        }}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(sale._id)}
                        sx={{
                          color: 'error.main',
                          '&:hover': { backgroundColor: 'error.light', color: 'white' }
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 1.5,
                    fontSize: '0.8rem',
                    lineHeight: 1.3,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {sale.description}
                </Typography>

                <Box display="flex" gap={0.5} mb={1.5} flexWrap="wrap">
                  <Chip
                    icon={<LocalOffer />}
                    label={`${sale.discountPercentage}% OFF`}
                    color="error"
                    size="small"
                    sx={{ fontSize: '0.7rem', height: '22px' }}
                  />
                  <Chip
                    icon={sale.isActive ? <Visibility /> : <VisibilityOff />}
                    label={sale.isActive ? 'Active' : 'Inactive'}
                    color={sale.isActive ? 'success' : 'default'}
                    size="small"
                    sx={{ fontSize: '0.7rem', height: '22px' }}
                  />
                  <Chip
                    icon={<Schedule />}
                    label={`P${sale.priority}`}
                    color="info"
                    size="small"
                    sx={{ fontSize: '0.7rem', height: '22px' }}
                  />
                </Box>

                <Box mb={1.5}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.3, fontSize: '0.7rem' }}>
                    <strong>Duration:</strong> {formatDate(sale.startDate)} - {formatDate(sale.endDate)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
                    <strong>Time Remaining:</strong> {getTimeRemaining(sale.endDate)}
                  </Typography>
                </Box>

                {sale.showCountdown && (
                  <Chip
                    label={`Countdown: ${sale.countdownPosition}`}
                    color="secondary"
                    size="small"
                    sx={{ mb: 1, fontSize: '0.7rem', height: '22px' }}
                  />
                )}

                <Box
                  sx={{
                    backgroundColor: sale.bannerColor,
                    color: sale.textColor,
                    p: 1,
                    borderRadius: 1,
                    textAlign: 'center',
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    letterSpacing: '0.3px',
                    textTransform: 'uppercase',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  }}
                >
                  {sale.discountPercentage}% OFF SALE
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Add/Edit Sale Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSale ? 'Edit Sale' : 'Add New Sale'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Discount Percentage"
                  name="discountPercentage"
                  type="number"
                  value={formData.discountPercentage}
                  onChange={handleChange}
                  inputProps={{ min: 0, max: 100 }}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Original Price"
                  name="originalPrice"
                  type="number"
                  value={formData.originalPrice}
                  onChange={handleChange}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Sale Price"
                  name="salePrice"
                  type="number"
                  value={formData.salePrice}
                  onChange={handleChange}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  name="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  name="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Priority"
                  name="priority"
                  type="number"
                  value={formData.priority}
                  onChange={handleChange}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Product (Navigate To)</InputLabel>
                  <Select
                    name="product"
                    value={formData.product || ''}
                    onChange={handleChange}
                    label="Product (Navigate To)"
                    disabled={productsLoading}
                  >
                    <MenuItem value="">
                      <em>None - Use Custom Link</em>
                    </MenuItem>
                    {allProducts.map((product) => (
                      <MenuItem key={product._id} value={product.slug || product._id}>
                        {product.name} {product.slug ? `(${product.slug})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Custom Link (if no product selected)"
                  name="link"
                  value={formData.link}
                  onChange={handleChange}
                  placeholder="/products?category=sale"
                  disabled={!!formData.product}
                  helperText={formData.product ? 'Product selected, link will be ignored' : 'Enter custom link or select a product above'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Banner Color"
                  name="bannerColor"
                  type="color"
                  value={formData.bannerColor}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Text Color"
                  name="textColor"
                  type="color"
                  value={formData.textColor}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Countdown Position</InputLabel>
                  <Select
                    name="countdownPosition"
                    value={formData.countdownPosition}
                    onChange={handleChange}
                  >
                    <MenuItem value="header">Header</MenuItem>
                    <MenuItem value="banner">Banner</MenuItem>
                    <MenuItem value="both">Both</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Image URL"
                  name="image"
                  value={formData.image}
                  onChange={handleChange}
                  placeholder="https://example.com/image.jpg"
                />
              </Grid>
              <Grid item xs={12}>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="sale-image-upload"
                  type="file"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                />
                <label htmlFor="sale-image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={uploadingImage ? <CircularProgress size={20} /> : <Add />}
                    disabled={uploadingImage}
                  >
                    {uploadingImage ? 'Uploading...' : 'Upload Image'}
                  </Button>
                </label>
                {imagePreview && (
                  <Box mt={2}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                    />
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={handleChange}
                      name="isActive"
                    />
                  }
                  label="Active"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.showCountdown}
                      onChange={handleChange}
                      name="showCountdown"
                    />
                  }
                  label="Show Countdown"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingSale ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Sales;
